'use client';

import { useEffect, useState } from 'react';

type GuideStickyTitleProps = {
  anchorId: string;
  title: string;
  backHref: string;
};

type GuideStickyBounds = {
  left: number;
  width: number;
};

export default function GuideStickyTitle({ anchorId, title, backHref }: GuideStickyTitleProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [bounds, setBounds] = useState<GuideStickyBounds | null>(null);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      const anchor = document.getElementById(anchorId);
      const card = anchor?.closest('.guide-detail-card');
      if (!anchor || !card) return;

      const anchorRect = anchor.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const nextBounds = {
        left: Math.round(cardRect.left),
        width: Math.round(cardRect.width),
      };
      setBounds((current) => {
        if (current && current.left === nextBounds.left && current.width === nextBounds.width) {
          return current;
        }
        return nextBounds;
      });
      setIsVisible((current) => {
        const next = anchorRect.bottom <= 0 && cardRect.bottom > 64;
        return current === next ? current : next;
      });
    };

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    // Capture scroll events from nested page containers as well as the window.
    document.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      document.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [anchorId, title]);

  if (!isVisible || !bounds) return null;

  return (
    <div
      className="guide-sticky-title"
      style={{ left: bounds.left, width: bounds.width }}
    >
      <a className="guide-sticky-back" href={backHref} aria-label="返回上一页" onClick={event => {
        if (window.history.length > 1) {
          event.preventDefault();
          window.history.back();
        }
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m14 6-6 6 6 6" /></svg>
      </a>
      <span className="guide-sticky-title-text">{title}</span>
    </div>
  );
}
